/**
 * One player row of the Stage — a card on `surface` with `sh1`, spanning
 * exactly the board frame.
 *
 * Measures since the clean-up of 2026-10-05 (R6.3): the row is 56 tall with 8/12
 * of padding and a 12 px radius (the same radius as the board frame and the
 * rail); avatar 36; name 15/SemiBold `ink`; "rating · colour" 12 `muted`; then
 * the captured tray and a 72x40 clock pill, right-aligned.
 *
 * The tray slot is shared with the FIRST-MOVE BANNER (decision D2), exactly as
 * the old row shares it (`PlayerComponent` L110-120): while the side on this row
 * still owes the game's opening move, "Ns to make the first move" takes the slot
 * and the tray is not mounted. It replaces the M1b dock chip, which was
 * anonymous ("Ns to make the first move" with no owner) and was shown to the
 * player who was WAITING for the move rather than the one who owed it.
 */
import React from 'react';
import {StyleSheet, View} from 'react-native';

import type {Side} from '@/Store/liveGame/state';

import {RADIUS, SPACE} from '../../theme/tokens';
import {useV2Theme} from '../../theme/useV2Theme';
import {LAYOUT} from '../../theme/useV2Ui';
import {Avatar, Txt} from '../../ui';
import Captured from '../Captured';
import Clock from '../Clock';
import FirstMoveBanner from './FirstMoveBanner';
import {PlayerRowModel} from './model';

export type PlayerRowViewProps = PlayerRowModel & {side: Side};

export default React.memo(function PlayerRowView({
  side,
  name,
  rating,
  colorLabel,
  isMe,
  firstMoveUp,
  hasSeat,
}: PlayerRowViewProps) {
  const tokens = useV2Theme(s => s.tokens);

  return (
    <View
      style={[
        styles.row,
        {
          backgroundColor: tokens.surface,
          borderColor: tokens.cardline,
          boxShadow: tokens.sh1,
        } as any,
      ]}>
      <Avatar name={name} size="row" tone={isMe ? 'ev' : 'alt'} compact />

      <View style={styles.identity}>
        <Txt variant="rowName" numberOfLines={1}>
          {name}
        </Txt>
        <Txt variant="captionTight" tone="muted" numeric numberOfLines={1}>
          {rating ? `${rating} · ${colorLabel}` : colorLabel}
        </Txt>
      </View>

      {firstMoveUp ? <FirstMoveBanner side={side} /> : <Captured side={side} />}
      {hasSeat && <Clock side={side} />}
    </View>
  );
});

const styles = StyleSheet.create({
  row: {
    // the row and `computeDesktopBoardSize` read the SAME number, or the column
    // overflows by the difference (R6.3: 56, down from M1a's 60)
    height: LAYOUT.playerRow,
    borderRadius: RADIUS.panel,
    borderWidth: 1,
    paddingVertical: SPACE.sm,
    paddingHorizontal: SPACE.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACE.md,
    alignSelf: 'stretch',
  },
  identity: {
    flex: 1,
    minWidth: 0,
  },
});
